import { NavigateBackLink } from '@exam-center/components';
import {
  ExamLocationManagementRoomRequest,
  ExamRoomRequestSubmitErrorAlert,
  useExamRoomRequestInfo,
} from '@exam-center/exam-location-management';
import {
  ExamLocationRequestWorkflowStepCode,
  GetExamRoomRequestInfoByIdDocument,
  useSubmitExamRoomRequestMutation,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useParams } from '@exam-center/router';
import {
  Flex,
  Text,
  PageHeader,
  Label,
  LabelColor,
  ConfirmationModal,
  Button,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import dayjs from 'dayjs';
import { useState } from 'react';

const STATUS_CONFIG: Partial<
  Record<ExamLocationRequestWorkflowStepCode, { label: string; color: LabelColor }>
> = {
  [ExamLocationRequestWorkflowStepCode.ExamLocationRequestRejected]: {
    label: 'ไม่อนุมัติ',
    color: LabelColor.ERROR,
  },
  [ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_1]: {
    label: 'อยู่ระหว่างอนุมัติ',
    color: LabelColor.WARNING,
  },
  [ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_2]: {
    label: 'อยู่ระหว่างอนุมัติ',
    color: LabelColor.WARNING,
  },
};

export default function ExamLocationRequestRoomPage() {
  const { examCenterCode, roomRequestId } = useParams(
    '/:examCenterCode/exam-location/requests/:roomRequestId',
  );

  const [isConfirmationModalOpen, { open, close }] = useDisclosure();
  const { showSnackbar } = useSnackbar();
  const { examRoomRequestInfo } = useExamRoomRequestInfo();
  const [submitExamRoomRequest, { loading }] = useSubmitExamRoomRequestMutation();

  const [submitExamRoomRequestResponse, setSubmitExamRoomRequestResponse] = useState<{
    isSuccess: boolean;
    hasComputerInfoError: boolean;
    hasPhysicalInfoError: boolean;
  } | null>(null);

  const handleSubmitExamRoomRequest = async () => {
    setSubmitExamRoomRequestResponse(null);
    await submitExamRoomRequest({
      variables: {
        submitExamRoomRequestInput: {
          id: roomRequestId,
        },
      },
      onCompleted: (res) => {
        close();
        if (res.ecSubmitExamRoomRequest.isSuccess) {
          showSnackbar({ message: 'ส่งคำขออนุมัติเรียบร้อยแล้ว', variant: 'success' });
          return;
        }
        showSnackbar({
          message: 'ไม่สามารถส่งคำขออนุมัติได้ กรุณากรอกข้อมูลให้ครบถ้วน',
          variant: 'error',
        });
        setSubmitExamRoomRequestResponse(res.ecSubmitExamRoomRequest);
      },
      refetchQueries: [GetExamRoomRequestInfoByIdDocument],
    });
  };

  const status = examRoomRequestInfo.step ? STATUS_CONFIG[examRoomRequestInfo.step] : undefined;

  const statusColor = status ? status.color : LabelColor.INFO;
  const statusLabel = status ? status.label : 'แบบร่าง';

  return (
    <>
      <PageHeader
        title={examRoomRequestInfo.examLocationRequestInfo.nameTh || 'รายละเอียดคำขอ'}
        linkAdornment={
          <NavigateBackLink
            to='/:examCenterCode/exam-location/requests'
            params={{ examCenterCode }}
            label='Back'
          />
        }
        endAdornment={<Label color={statusColor}>{statusLabel}</Label>}
        subtitleSection={
          <Flex sx={{ width: '100%', justifyContent: 'space-between' }}>
            <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
              <Text variant='body2'>เลขที่คำขอ: {examRoomRequestInfo.taskCode ?? '-'}</Text>
              <Text variant='body2'>ศูนย์ทดสอบ: {examCenterCode}</Text>
              <Text variant='body2'>
                วันที่ส่งคำขอ:{' '}
                {examRoomRequestInfo.requestedAt
                  ? dayjs(examRoomRequestInfo.requestedAt).format('DD-MM-YYYY, HH:mm')
                  : '-'}
              </Text>
            </Flex>
            <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
              <Text variant='body2'>
                Last Updated at:{' '}
                {dayjs(examRoomRequestInfo.examLocationRequestInfo.editedAt).format(
                  'DD-MM-YYYY, HH:mm',
                )}
              </Text>
              <Text variant='body2'>
                By: {examRoomRequestInfo.examLocationRequestInfo.editedUser?.firstname ?? '-'}{' '}
                {examRoomRequestInfo.examLocationRequestInfo.editedUser?.lastname ?? ''}
              </Text>
            </Flex>
          </Flex>
        }
      />
      <ExamRoomRequestSubmitErrorAlert
        examCenterCode={examCenterCode}
        roomRequestId={roomRequestId}
        hasComputerInfoError={submitExamRoomRequestResponse?.hasComputerInfoError ?? false}
        hasPhysicalInfoError={submitExamRoomRequestResponse?.hasPhysicalInfoError ?? false}
      />
      <ExamLocationManagementRoomRequest
        examCenterCode={examCenterCode}
        requestId={examRoomRequestInfo.examLocationRequestInfoId}
        roomRequestId={roomRequestId}
      />
      {examRoomRequestInfo.step ===
      ExamLocationRequestWorkflowStepCode.ExamLocationRequestRejected ? (
        <WithPermission
          requiredPermission={[
            APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.CREATE,
          ]}
        >
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              alignItems: 'flex-end',
              p: theme.spacing(6),
              pt: theme.spacing(0),
            })}
          >
            <Button onClick={() => open()} size='large'>
              ส่งคำขออนุมัติอีกครั้ง
            </Button>
          </Flex>
          <ConfirmationModal
            open={isConfirmationModalOpen}
            title='ยืนยันการส่งคำขออนุมัติ'
            content={
              <Text variant='body1'>
                เมื่อส่งคำขอแล้ว{' '}
                <Text component='span' variant='body1Semibold'>
                  จะไม่สามารถแก้ไขข้อมูลได้อีก
                </Text>
                กรุณาตรวจสอบให้แน่ใจก่อนดำเนินการ
              </Text>
            }
            onClose={close}
            onDiscard={close}
            onAccept={handleSubmitExamRoomRequest}
            loading={loading}
            acceptText='ส่งคำขออนุมัติ'
            discardText='ยกเลิก'
          />
        </WithPermission>
      ) : null}
    </>
  );
}
